/**
 * Node Type Filter Definition
 *
 * Filters nodes by their type (Bhajan, Ragam, Talam, etc.)
 */

import { NodeTypeId, NodeTypeNames, GraphDataNode } from '../../types/graph.types';
import {
  FilterDefinition,
  FilterOperator,
  FilterValueType,
  FilterSuggestion,
  FilterValue,
} from '../types';

/**
 * Get all node type options for the filter
 */
const nodeTypeOptions = Object.entries(NodeTypeNames).map(([id, name]) => ({
  value: parseInt(id, 10) as NodeTypeId,
  label: name,
}));

/**
 * Node Type Filter Definition
 */
export const nodeTypeFilter: FilterDefinition = {
  id: 'nodeType',
  label: 'Node Type',
  shortLabel: 'Type',
  icon: 'TagIcon',
  valueType: FilterValueType.NodeType,
  supportedOperators: [FilterOperator.Is, FilterOperator.In, FilterOperator.NotIs, FilterOperator.NotIn],
  defaultOperator: FilterOperator.Is,
  options: nodeTypeOptions,
  placeholder: 'Select node type...',

  suggestMatch(query: string, _nodes: GraphDataNode[]): FilterSuggestion[] {
    const normalizedQuery = query.toLowerCase().trim();
    if (!normalizedQuery) {
      return [];
    }

    const suggestions: FilterSuggestion[] = [];

    // Match against node type names
    for (const [id, name] of Object.entries(NodeTypeNames)) {
      const normalizedName = name.toLowerCase();
      const nodeTypeId = parseInt(id, 10) as NodeTypeId;

      // Check for prefix or substring match
      if (normalizedName.startsWith(normalizedQuery)) {
        suggestions.push({
          filterId: 'nodeType',
          label: `Type: ${name}`,
          description: `Show only ${name} nodes`,
          prefilledOperator: FilterOperator.Is,
          prefilledValue: nodeTypeId,
          needsConfiguration: false,
          score: 100 - normalizedQuery.length + (normalizedName === normalizedQuery ? 50 : 0),
        });
      } else if (normalizedName.includes(normalizedQuery)) {
        suggestions.push({
          filterId: 'nodeType',
          label: `Type: ${name}`,
          description: `Show only ${name} nodes`,
          prefilledOperator: FilterOperator.Is,
          prefilledValue: nodeTypeId,
          needsConfiguration: false,
          score: 50,
        });
      }
    }

    // If query looks like a type but doesn't match exactly, suggest generic type filter
    if (suggestions.length === 0 && normalizedQuery.length >= 2) {
      // Check for partial matches with common type keywords
      const typeKeywords = ['type', 'kind', 'category'];
      if (typeKeywords.some((kw) => kw.includes(normalizedQuery) || normalizedQuery.includes(kw))) {
        suggestions.push({
          filterId: 'nodeType',
          label: 'Type: ...',
          description: 'Filter by node type',
          prefilledOperator: FilterOperator.Is,
          prefilledValue: NodeTypeId.BHAJAN, // Default
          needsConfiguration: true,
          score: 30,
        });
      }
    }

    return suggestions.sort((a, b) => b.score - a.score);
  },

  execute(node: GraphDataNode, operator: FilterOperator, value: FilterValue): boolean {
    const nodeTypeId = node.nodeTypeId;

    switch (operator) {
      case FilterOperator.Is:
        return nodeTypeId === value;

      case FilterOperator.NotIs:
        return nodeTypeId !== value;

      case FilterOperator.In:
        if (Array.isArray(value)) {
          return (value as number[]).includes(nodeTypeId);
        }
        return nodeTypeId === value;

      case FilterOperator.NotIn:
        if (Array.isArray(value)) {
          return !(value as number[]).includes(nodeTypeId);
        }
        return nodeTypeId !== value;

      default:
        return true;
    }
  },

  getDisplayValue(value: FilterValue): string {
    if (Array.isArray(value)) {
      return value.map((v) => NodeTypeNames[v as NodeTypeId] || `Type ${v}`).join(', ');
    }
    return NodeTypeNames[value as NodeTypeId] || `Type ${value}`;
  },
};
